import React, { useContext, useMemo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Container, Spinner } from 'react-bootstrap';
import { BiLandscape } from 'react-icons/bi';
import AppContext from 'Utils/AppContext';
import UnknownSourcesTable from 'SensitiveDataLandscape/UnknownSources/UnknownSourcesTable';
import FetchContainer from 'Utils/FetchContainer';
import PageHeader from 'Utils/PageHeader';
import { normalizeEngineToConnectionType } from 'Utils';
import { getLandscapeHomeSummary, mapSummaryToUnknownSources } from 'SensitiveDataLandscape/homeSummaryApi';
import { getManagedSources, kindForSource } from 'AssetManager/LandscapeUtils';
import {
  normalizeHost,
  getConnectionHost,
  isAlreadyADataSource,
  managerSourceToRow,
} from 'SensitiveDataLandscape/UnknownSources/managerSources';

const pickLatestConnection = (connections = []) => {
  if (!Array.isArray(connections) || !connections.length) return null;
  return connections.reduce((latest, conn) => {
    if (!latest) return conn;
    const latestTs = Date.parse(latest.creationTime || latest.createdAt || 0);
    const connTs = Date.parse(conn.creationTime || conn.createdAt || 0);
    return connTs > latestTs ? conn : latest;
  }, null);
};

const firstDefined = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

const getConnectionPort = (connection = {}) => firstDefined(connection?.port, connection?.config?.port, -1);
const getConnectionDatabase = (connection = {}) => firstDefined(connection?.database, connection?.config?.database, connection?.dbName, '');
const getConnectionSchema = (connection = {}) => firstDefined(connection?.schemaName, connection?.schema, connection?.config?.schema, '');
const getConnectionUser = (connection = {}) => firstDefined(connection?.user, connection?.username, connection?.config?.username, '');

const resolveConnection = (source, connectionsList = [], hostToConnectionsMap = {}) => {
  const sourceConnections = Array.isArray(source.connections) ? source.connections.filter(Boolean) : [];
  const byName = connectionsList.filter(conn => sourceConnections.includes(conn.name));
  if (byName.length) return pickLatestConnection(byName);

  const sourceNameCandidates = [source?.name, source?.host].filter(Boolean);
  if (sourceNameCandidates.length) {
    const bySourceName = connectionsList.filter(conn => sourceNameCandidates.includes(conn?.name));
    if (bySourceName.length) return pickLatestConnection(bySourceName);
  }

  const mappedName = hostToConnectionsMap[source.host];
  if (mappedName) {
    const byMappedName = connectionsList.filter(conn => conn.name === mappedName);
    if (byMappedName.length) return pickLatestConnection(byMappedName);
  }

  const normalizedSourceHost = normalizeHost(source.host);
  const byHost = connectionsList.filter(conn => normalizeHost(getConnectionHost(conn)) === normalizedSourceHost);
  if (byHost.length) return pickLatestConnection(byHost);

  return null;
};

const getUnscannedSourcesData = (unscannedSources, defaultRules, hostToConnectionsMap, connectionsList, region) => {
  const result = [];

  if (unscannedSources) {
    unscannedSources.forEach(source => {
      const engine = source.engine;
      const matchedConnection = resolveConnection(source, connectionsList, hostToConnectionsMap);

      const item = {
        ...source,
        id: firstDefined(matchedConnection?.id, source?.id, null),
        tableData: firstDefined(
          matchedConnection?.id != null ? { id: matchedConnection.id } : null,
          source?.id != null ? { id: source.id } : null,
          undefined
        ),
        connectionName: firstDefined(matchedConnection?.name, source?.connections?.[0], source?.name, ''),
        connections: firstDefined(
          matchedConnection?.name ? [matchedConnection.name] : null,
          Array.isArray(source?.connections) ? source.connections : null,
          []
        ),
        port: firstDefined(getConnectionPort(matchedConnection), source.port, -1),
        database: firstDefined(getConnectionDatabase(matchedConnection), source.database, ''),
        schema: firstDefined(getConnectionSchema(matchedConnection), source.schema, ''),
        user: firstDefined(getConnectionUser(matchedConnection), source.user, ''),
        // IP/DNS column: file sources show their object-store URL (rootPath), databases
        // show their endpoint host/DNS. Prefer the matched data source's stored value.
        ipDns: firstDefined(
          matchedConnection?.rootPath,
          getConnectionHost(matchedConnection),
          source.rootPath,
          source.host,
          '',
        ) || '',
        runSearch: true,
        scheduleExpression: '0 0 ? * SAT#1 *',
        scheduleRules: defaultRules,
        type: normalizeEngineToConnectionType(engine)
      };
      result.push(item);
    })
    // Category-based so cloud-specific source keys (azure_sql, gcp_sql, …) still match the
    // legacy tile filters. Mirrors managerRowMatchesRegion below and mapSummaryToUnknownSources.
    if (region === 'ec2') return result.filter(o => kindForSource(o.source) === 'servers');
    if (region === 'rds') return result.filter(o => kindForSource(o.source) === 'rdbm');
    if (region === 's3') return result.filter(o => kindForSource(o.source) === 'files');
    if (region === 'snowflake') return result.filter(o => o.engine === 'snowflake');
    if (region === 'mongodb') return result.filter(o => o.engine === 'mongodb');
    if (region !== 'all') return result.filter(o => o.region === region);
  }

  return result;
}

// Manager source keys map onto the legacy region filters the undiscovered page supports.
// Potential rows keep their real manager source key (azure_sql, gcp_sql, …), so the
// legacy rds/ec2/s3 filters map by category (kindForSource) rather than exact key.
const managerRowMatchesRegion = (row, region) => {
  if (!region || region === 'all') return true;
  if (region === 'ec2') return kindForSource(row.source) === 'servers';
  if (region === 'rds') return kindForSource(row.source) === 'rdbm';
  if (region === 's3') return kindForSource(row.source) === 'files';
  if (region === 'snowflake') return row.engine === 'snowflake';
  if (region === 'mongodb') return row.engine === 'mongodb';
  return row.region === region;
};

const UnknownSources = ({ fetchingData, unscannedSources, error}) => {
  const { region } = useParams();
  const { rulesList, hostToConnectionsMap, connections } = useContext(AppContext);
  const [homeSummary, setHomeSummary] = useState(null);
  const [managedSources, setManagedSources] = useState([]);
  const defaultRules = rulesList.find(rules => rules.default === true) || rulesList[0];
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const response = await getLandscapeHomeSummary();
        setHomeSummary(response);
      } catch (e) {
        console.log('Error loading landscape home summary for UnknownSources', e);
      }
    };
    // Best-effort: the manager landscape augments the list with cloud resources that
    // aren't data sources yet. getManagedSources already swallows errors and returns an
    // empty list, so a manager outage just falls back to the existing rows.
    const loadManaged = async () => {
      try {
        const landscape = await getManagedSources();
        setManagedSources(Array.isArray(landscape?.sources) ? landscape.sources : []);
      } catch (e) {
        console.log('Error loading managed landscape for UnknownSources', e);
      }
    };
    loadSummary();
    loadManaged();
  }, []);

  const unscannedSourcesData = useMemo(() => {
    const existingRows = homeSummary
      ? getUnscannedSourcesData(
          mapSummaryToUnknownSources(homeSummary, region),
          defaultRules,
          hostToConnectionsMap,
          connections,
          region
        )
      : getUnscannedSourcesData(unscannedSources.sources, defaultRules, hostToConnectionsMap, connections, region);

    // Dedup manager resources against both saved connections and the rows already on the
    // page, matching on host + every IP/DNS variant the resource exposes.
    const existingHosts = new Set();
    connections.forEach((conn) => {
      const h = normalizeHost(getConnectionHost(conn));
      if (h) existingHosts.add(h);
    });
    existingRows.forEach((row) => {
      const h = normalizeHost(row.host);
      if (h) existingHosts.add(h);
    });

    const potentialRows = managedSources
      .filter((resource) => !isAlreadyADataSource(resource, existingHosts))
      .map(managerSourceToRow)
      .filter((row) => managerRowMatchesRegion(row, region));

    return existingRows.concat(potentialRows);
  }, [homeSummary, unscannedSources, defaultRules, hostToConnectionsMap, connections, region, managedSources]);

  return (
    <FetchContainer fluid={true} fetchingData={fetchingData}>
      <div>
        <PageHeader 
          header='Undiscovered Data Sources'
          icon={BiLandscape}
          tooltip='Data sources that have not had a discovery run yet, plus potential data sources detected across your cloud accounts that are not data sources yet. Use Create Data Source to turn a detected resource into a data source.'
          disabledActions={true}
        />
        { error &&
          <Alert variant='danger'>
            {error}
          </Alert>
        }
        <div className='d-flex flex-wrap'>
          <UnknownSourcesTable
            unscannedSources={unscannedSourcesData}
          />
        </div>
      </div>
    </FetchContainer>
  );
}

export default UnknownSources;
